import React from "react";
import { Form, Button, Row, Col, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { useCreateTimelineMutation } from "../../../redux/api/TimelineApi/timelineApi";
import { FaPlus, FaSave, FaArrowLeft, FaImage } from "react-icons/fa";
import "../Services/Services.css";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import { useFormik } from "formik";
import * as Yup from "yup";

const AddTimeline = () => {
  const navigate = useNavigate();
  const [createTimeline, { isLoading }] = useCreateTimelineMutation();

  const validationSchema = Yup.object({
    timeline_year: Yup.string().trim().required("Year is required"),
    timeline_date: Yup.date().required("Date is required"),
    timeline_description: Yup.string()
      .trim()
      .required("Description is required"),
    position: Yup.string()
      .oneOf(["left", "right"])
      .required("Position is required"),
  });

  const formik = useFormik({
    initialValues: {
      timeline_year: "",
      timeline_date: "",
      timeline_step: "",
      timeline_title: "",
      timeline_description: "",
      icon: "Foundation",
      color: "#4F46E5",
      position: "right",
      status: true,
      image: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        if (key === "image" && values[key]) {
          formData.append("image", values[key]);
        } else if (key === "status") {
          formData.append("status", values[key] ? "1" : "0");
        } else {
          formData.append(key, values[key] || "");
        }
      });

      try {
        await createTimeline(formData).unwrap();
        message.success("Milestone created successfully!");
        setTimeout(() => navigate("/admin/timeline"), 2000);
      } catch (error) {
        message.error(
          error.data?.error || error.message || "Failed to create milestone",
        );
      }
    },
  });

  const handleFileChange = (event) => {
    formik.setFieldValue("image", event.currentTarget.files[0]);
  };

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-light"
        style={{ height: "100vh" }}
      >
        <LoaderSpinner />
      </div>
    );
  }

  return (
    <div className="container py-4 fade-in">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <FaPlus className="text-primary me-2" />
            Add Milestone
          </h2>
          <p className="text-muted mb-0">
            Record a new milestone in your company's history
          </p>
        </div>
        <Button
          variant="outline-secondary"
          className="d-flex align-items-center gap-2 shadow-sm rounded-pill px-4"
          onClick={() => navigate("/admin/timeline")}
        >
          <FaArrowLeft /> Back to List
        </Button>
      </div>

      <Form onSubmit={formik.handleSubmit}>
        <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
          <Card.Body className="p-4 p-md-5">
            <h5 className="mb-4 text-secondary border-start border-4 border-primary ps-3">
              Milestone Details
            </h5>
            <Row className="g-4">
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Year <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="timeline_year"
                    placeholder="e.g. 2025"
                    {...formik.getFieldProps("timeline_year")}
                    isInvalid={
                      formik.touched.timeline_year &&
                      !!formik.errors.timeline_year
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.timeline_year}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Exact Date <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    name="timeline_date"
                    {...formik.getFieldProps("timeline_date")}
                    isInvalid={
                      formik.touched.timeline_date &&
                      !!formik.errors.timeline_date
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.timeline_date}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Step Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="timeline_step"
                    placeholder="e.g. 01"
                    {...formik.getFieldProps("timeline_step")}
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Position <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="position"
                    {...formik.getFieldProps("position")}
                  >
                    <option value="right">Right</option>
                    <option value="left">Left</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Milestone Title
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="timeline_title"
                    placeholder="Brief title for this milestone..."
                    {...formik.getFieldProps("timeline_title")}
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Description <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="timeline_description"
                    placeholder="Tell the story of this milestone..."
                    {...formik.getFieldProps("timeline_description")}
                    isInvalid={
                      formik.touched.timeline_description &&
                      !!formik.errors.timeline_description
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.timeline_description}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Icon</Form.Label>
                  <Form.Select name="icon" {...formik.getFieldProps("icon")}>
                    <option value="Foundation">Foundation (Star)</option>
                    <option value="Client">Client (Person)</option>
                    <option value="Growth">Growth (Trend Map)</option>
                    <option value="Award">Award (Trophy)</option>
                    <option value="Global">Global (Earth)</option>
                    <option value="Digital">Digital (Comp)</option>
                    <option value="Sustainability">
                      Sustainability (Leaf)
                    </option>
                    <option value="Leadership">Leadership (Mic)</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Theme Color</Form.Label>
                  <div className="d-flex gap-2 align-items-center">
                    <Form.Control
                      type="color"
                      name="color"
                      {...formik.getFieldProps("color")}
                      style={{ width: "50px", height: "38px", padding: "2px" }}
                    />
                    <Form.Control
                      type="text"
                      name="color"
                      {...formik.getFieldProps("color")}
                      placeholder="#000000"
                    />
                  </div>
                </Form.Group>
              </Col>

              <Col md={4} className="d-flex align-items-center mt-md-auto mt-4">
                <Form.Check
                  type="switch"
                  id="status-switch"
                  label="Publish to Website"
                  name="status"
                  checked={formik.values.status}
                  onChange={formik.handleChange}
                  className="fw-bold text-primary"
                />
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Display Image</Form.Label>
                  <div className="custom-file-container">
                    <input
                      type="file"
                      id="timeline-image-add"
                      accept="image/*"
                      onChange={handleFileChange}
                      hidden
                    />
                    <label
                      htmlFor="timeline-image-add"
                      className="d-flex align-items-center gap-2 border rounded-3 p-1 cursor-pointer bg-white shadow-sm"
                      style={{ minHeight: "45px" }}
                    >
                      <div className="btn btn-primary btn-sm rounded-pill px-3 ms-1">
                        Choose File
                      </div>
                      <span className="text-muted small text-truncate px-2 flex-grow-1">
                        {formik.values.image
                          ? formik.values.image.name
                          : "No file chosen"}
                      </span>
                      <div className="pe-3 text-primary">
                        <FaImage />
                      </div>
                    </label>
                  </div>
                  {formik.values.image && (
                    <div
                      className="mt-3 p-2 bg-light rounded text-center"
                      style={{ maxWidth: "200px" }}
                    >
                      <img
                        src={URL.createObjectURL(formik.values.image)}
                        alt={formik.values.image.name}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "120px",
                          objectFit: "contain",
                        }}
                      />
                      <div className="small text-muted mt-1">
                        {formik.values.image.name}
                      </div>
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-3 mt-5">
              <Button
                variant="light"
                className="rounded-pill px-4"
                onClick={() => navigate("/admin/timeline")}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                className="rounded-pill px-5 shadow-sm d-flex align-items-center gap-2"
              >
                <FaSave /> Save Milestone
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Form>
    </div>
  );
};

export default AddTimeline;
